import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { ActiveIncentiveView } from '@vello/api-client';
import { countdownOf, formatMarketInstant, marketTimeZone, offerSpendBp, offerStateTone, splitMinutes } from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, EmptyState, Notice, Panel, Stack, Table, Text, useAutoRefresh } from '@vello/web-theme';
import { offerLine, wireMoney } from './shared';
import { useNow } from './use-now';

/**
 * The offers in force (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 section 3.4): every incentive the market is making or has scheduled - the operator's own (MANUAL) and the
 * controller's (CONTROLLER) in ONE list, each with a source chip, the offer in words, what it has spent of its cap and the time left until it expires (a countdown, with the exact end in the
 * market's time zone). Read only: proposing, approving and ending an offer stay on the Offers screen. A refused read is an ERROR, never an empty list.
 */
const SHOWN: readonly string[] = ['IN_FORCE', 'SCHEDULED'];

export function ActiveOffersPanel({ market, zoneNames }: { readonly market: string; readonly zoneNames: ReadonlyMap<string, string> }): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const [offers, setOffers] = useState<readonly ActiveIncentiveView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const nowMs = useNow();
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '') {
      setOffers(undefined);
      return;
    }
    const res = await runtime.activeIncentives.list(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setOffers(res.value.incentives.filter((o) => SHOWN.includes(o.state)));
      setError(undefined);
    } else {
      setOffers(undefined);
      setError(t('cpt.offers.error', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail]);

  useEffect(() => {
    setOffers(undefined);
    setError(undefined);
    void load();
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(market, markets);
  const money = (m: { readonly amountMinor: string; readonly currency: string }): string => wireMoney(m, lang);
  const zoneLabel = (zoneId: string): string => (zoneId === 'MARKET' ? t('ctl.zone.market') : zoneNames.has(zoneId) ? t('ctl.zone.drawn', { name: zoneNames.get(zoneId) ?? '' }) : t('ctl.zone.unknown'));
  const left = (o: ActiveIncentiveView): string => {
    const c = countdownOf(o.validFrom, o.validTo, nowMs);
    if (c.state === 'ENDED') return t('cpt.offers.ended');
    const { days, hours, minutes } = splitMinutes(c.minutes);
    const span = days > 0 ? t('cpt.offers.span.days', { days, hours }) : hours > 0 ? t('cpt.offers.span.hours', { hours, minutes }) : t('cpt.offers.span.minutes', { minutes });
    return c.state === 'SCHEDULED' ? t('cpt.offers.startsIn', { span }) : t('cpt.offers.endsIn', { span });
  };

  return (
    <Panel padded={false}>
      <Stack gap={3}>
        <h3>{t('cpt.offers.title')}</h3>
        <Text role="caption" tone="tertiary">{t('cpt.offers.hint')}</Text>
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {error === undefined && (
          <Table
            columns={[
              { key: 'source', header: t('cpt.offers.col.source'), render: (o: ActiveIncentiveView) => <Badge tone={o.source === 'CONTROLLER' ? 'success' : 'neutral'}>{v('active_incentive.source', o.source)}</Badge> },
              { key: 'offer', header: t('cpt.offers.col.offer'), render: (o: ActiveIncentiveView) => <span>{offerLine(o, t, v, money)}</span> },
              { key: 'zone', header: t('cpt.offers.col.zone'), render: (o: ActiveIncentiveView) => <span>{zoneLabel(o.zoneId)}</span> },
              { key: 'state', header: t('cpt.offers.col.state'), render: (o: ActiveIncentiveView) => <Badge tone={offerStateTone(o.state)}>{v('active_incentive.state', o.state)}</Badge> },
              {
                key: 'spent', header: t('cpt.offers.col.spent'), numeric: true,
                render: (o: ActiveIncentiveView) => <span className="tnum">{t('cpt.offers.spent', { spent: money(o.spent), cap: money(o.spendCap), pct: Math.round(offerSpendBp(o) / 100) })}</span>,
              },
              {
                key: 'ends', header: t('cpt.offers.col.ends'),
                render: (o: ActiveIncentiveView) => (
                  <Stack gap={1}>
                    <span className="tnum">{left(o)}</span>
                    <Text role="caption" tone="tertiary"><span className="tnum">{formatMarketInstant(o.validTo, tz, lang)}</span></Text>
                  </Stack>
                ),
              },
            ]}
            rows={offers ?? []}
            rowKey={(o) => o.incentiveId}
            empty={<EmptyState title={offers === undefined ? t('ui.loading') : t('cpt.offers.empty')} />}
          />
        )}
      </Stack>
    </Panel>
  );
}
